import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  message,
  Tag,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import * as Icons from "@ant-design/icons";
import {
  fetchTunjangan,
  createTunjangan,
  updateTunjangan,
  deleteTunjangan,
} from "../../api/master/TunjanganKaryawan";
import { dropdownKaryawan } from "../../api/master/Karyawan";

const { Option } = Select;

const TunjanganKaryawanPage = () => {
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  const [editForm] = Form.useForm();
  const [modalForm] = Form.useForm();

  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [karyawanList, SetKaryawanList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<Icons.SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Cari
        </Button>
        <Button
          onClick={() => {
            handleReset(clearFilters);
            confirm({ closeDropdown: true });
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icons.SearchOutlined
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: "#ffc069", padding: 0 }}>{text}</span>
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const apiData = await fetchTunjangan();
      const mappedData = apiData.map((item) => ({
        key: item.tjk_id.toString(),
        emp_nama: item.emp_nama,
        keterangan: item.tjk_keterangan.trim(),
        jumlah: Number(item.tjk_jumlah),
        empId: item.tjk_karyawan_id,
        status: item.tjk_status,
        periode: item.tjk_periode,
      }));

      setData(mappedData);
    } catch (error) {
      message.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const loadMasterKaryawan = async () => {
    try {
      const res = await dropdownKaryawan();
      if (res.data) {
        SetKaryawanList(res.data);
      } else {
        message.error("Gagal memuat data Karyawan");
      }
    } catch (error) {
      message.error("Gagal memuat data");
    }
  };

  useEffect(() => {
    loadData();
    loadMasterKaryawan();
  }, []);

  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    editForm.setFieldsValue({
      ...record,
      periode: dayjs(record.periode, "YYYY-MM"),
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key) => {
    setSaving(true);
    try {
      const row = await editForm.validateFields();
      const item = data.find((d) => d.key === key);
      const payload = {
        tjk_keterangan: row.keterangan,
        tjk_jumlah: row.jumlah,
        tjk_periode: row.periode.format
          ? row.periode.format("YYYY-MM")
          : row.periode,
        tjk_status: row.status,
        tjk_updated_by: sessiData[0].user[0].fullname,
      };

      const result = await updateTunjangan(item.key, payload);
      if (result.status) {
        message.success("Tunjangan Karyawan berhasil diperbarui");
        setEditingKey("");
        await loadData();
      } else {
        message.error("Gagal memperbarui Tunjangan Karyawan");
      }
    } catch (err) {
      message.error("Terjadi kesalahan validasi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    try {
      const result = await deleteTunjangan(key);
      if (result.status) {
        message.success("Tunjangan Karyawan berhasil dihapus");
        loadData();
      } else {
        message.error("Gagal menghapus Tunjangan Karyawan");
      }
    } catch (error) {
      message.error("Terjadi kesalahan saat menghapus");
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    modalForm.resetFields();
  };

  const handleModalOk = async () => {
    setSaving(true);
    try {
      const values = await modalForm.validateFields();
      const payload = {
        tjk_karyawan_id: values.empId,
        tjk_periode: values.periode.format("YYYY-MM"),
        tjk_keterangan: values.keterangan,
        tjk_jumlah: values.jumlah,
        tjk_status: values.status,
        tjk_created_by: sessiData[0].user[0].fullname,
      };

      const result = await createTunjangan(payload);
      if (result.status) {
        message.success("Tunjangan Karyawan berhasil ditambahkan");
        modalForm.resetFields();
        setIsModalVisible(false);
        await loadData();
      } else {
        message.error("Gagal menambahkan Tunjangan Karyawan");
      }
    } catch (error) {
      message.error("Terjadi kesalahan saat menambahkan");
    } finally {
      setSaving(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    modalForm.resetFields();
  };

  const getColumnMonthFilterProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <DatePicker
          picker="month"
          value={selectedKeys[0] ? dayjs(selectedKeys[0], "YYYY-MM") : null}
          onChange={(date, dateString) => {
            setSelectedKeys(dateString ? [dateString] : []);
          }}
          style={{ width: "100%", marginBottom: 8 }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<Icons.SearchOutlined />}
          size="small"
          style={{ width: "100%", marginBottom: 8 }}
        >
          Cari
        </Button>
        <Button
          onClick={() => {
            clearFilters();
            confirm({ closeDropdown: true });
          }}
          size="small"
          style={{ width: "100%" }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icons.CalendarOutlined
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) => record[dataIndex]?.startsWith(value),
    render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
  });

  const columns = [
    {
      title: "Nama Karyawan",
      dataIndex: "emp_nama",
      key: "emp_nama",
      ...getColumnSearchProps("emp_nama"),
    },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
      editable: true,
    },
    {
      title: "Jumlah",
      dataIndex: "jumlah",
      editable: true,
      render: (val) => `Rp ${val.toLocaleString()}`,
      sorter: (a, b) => a.jumlah - b.jumlah,
    },
    {
      title: "Periode",
      dataIndex: "periode",
      key: "periode",
      editable: true,
      ...getColumnMonthFilterProps("periode"),
      render: (val) => (val ? dayjs(val).format("MMMM YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      render: (val) => (
        <Tag color={val === "Aktif" ? "green" : "red"}>{val}</Tag>
      ),
      filters: [
        { text: "Aktif", value: "Aktif" },
        { text: "Tidak Aktif", value: "Tidak Aktif" },
      ],
      onFilter: (value, record) => record.status === value,
    },

    {
      title: "Aksi",
      dataIndex: "aksi",
      width: 100,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              onClick={() => save(record.key)}
              type="link"
              loading={saving}
            >
              Simpan
            </Button>
            <Button onClick={cancel} type="link">
              Batal
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              type="link"
            >
              Edit
            </Button>
            <Popconfirm
              title="Yakin ingin menghapus?"
              onConfirm={() => handleDelete(record.key)}
            >
              <Button type="link" danger>
                Hapus
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === "jumlah" ? "number" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    let inputNode;
    if (dataIndex === "status") {
      inputNode = (
        <Select>
          <Option value="Aktif">Aktif</Option>
          <Option value="Tidak Aktif">Tidak Aktif</Option>
        </Select>
      );
    } else if (dataIndex === "periode") {
      inputNode = (
        <DatePicker picker="month" format="YYYY-MM" style={{ width: "100%" }} />
      );
    } else {
      inputNode =
        inputType === "number" ? (
          <InputNumber
            min={0}
            step={0.01}
            style={{ width: "100%" }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
            }
            parser={(value) => value.replace(/Rp\s?|(,*)/g, "")}
          />
        ) : (
          <Input />
        );
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `Isi ${title.toLowerCase()}` }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={showModal}>
          Tambah Tunjangan Karyawan
        </Button>
      </div>

      <Form form={editForm} component={false}>
        <Table
          loading={loading}
          components={{ body: { cell: EditableCell } }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            pageSizeOptions: ["10", "20", "40", "60"],
            showSizeChanger: true,
            defaultPageSize: 20,
          }}
          scroll={{ x: "max-content" }}
        />
      </Form>

      <Modal
        title="Tambah Tunjangan Karyawan"
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Simpan"
        cancelText="Batal"
        confirmLoading={saving}
      >
        <Form form={modalForm} layout="vertical">
          <Form.Item
            name="empId"
            label="Nama Karyawan"
            rules={[{ required: true, message: "Pilih Karyawan!" }]}
          >
            <Select
              showSearch
              placeholder="Pilih Karyawan"
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {karyawanList.map((k) => (
                <Option key={k.emp_id_karyawan} value={k.emp_id_karyawan}>
                  {k.emp_nama}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Nama Tunjangan" name="keterangan">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="jumlah"
            label="Jumlah"
            rules={[{ required: true, message: "Masukkan jumlah tunjangan!" }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              formatter={(value) =>
                value ? `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => value.replace(/Rp\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            name="periode"
            label="Periode"
            rules={[{ required: true }]}
          >
            <DatePicker
              picker="month"
              style={{ width: "100%" }}
              // value={periodeSelected}
              // onChange={handlePeriodeChange}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Pilih status!" }]}
          >
            <Select placeholder="Pilih Status">
              <Option value="Aktif">Aktif</Option>
              <Option value="Tidak Aktif">Tidak Aktif</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TunjanganKaryawanPage;
